/**
 * API types — mirrors the 1TOALL portal backend response shapes (the source of truth is
 * app/modules/<m>/router.py + service.py in the 1toall-portal-api repo). Money-bearing fields are
 * OPTIONAL here on purpose: the server STRIPS them (keys ABSENT, not null) for roles without
 * money:read, so the UI must treat their absence as normal and never crash on it. We never infer
 * money visibility from a missing field — only from `sees_money` on /me.
 *
 * Base URL: /api/v1 (health is unprefixed). Bearer JWT on every authenticated call.
 */

// The one import in this file, and deliberately type-only: `RenewalFailure` is the token-renewal verdict
// that rides on ApiError (see the bottom of this file). tokenRenewal.ts is a React-free, Auth0-free leaf
// module, so this adds no runtime coupling and no import cycle.
import type { RenewalFailure } from "@/context/tokenRenewal";

// ---------------------------------------------------------------------------------------------------
// Roles & permissions
// ---------------------------------------------------------------------------------------------------

export type Role = "owner" | "admin" | "viewer" | "custom";

/** Stable permission keys (rbac/catalog.py). */
export type Permission =
  | "search:read"
  | "customer:read"
  | "money:read"
  | "worklist:read"
  | "audit:read"
  | "user:manage"
  | "config:manage";

/** GET /api/v1/me */
export interface Me {
  id: string;
  email: string;
  /** Staff display name (Auth0 name claim); may be null → callers fall back to the email local-part. */
  name: string | null;
  role: Role;
  permissions: Permission[];
  /** TRUST this for money visibility — never infer it from a missing money field. */
  sees_money: boolean;
}

// ---------------------------------------------------------------------------------------------------
// Search (GET /api/v1/search?q=)
// ---------------------------------------------------------------------------------------------------

/**
 * Zoom's account-retirement flags, mirrored onto sub_accounts by the 15-min sweep (API migration 0016)
 * and read from that BASE TABLE — never from v_customer_360, whose definition view_sync re-pulls from
 * ZPCD daily (the portal can override that view's values, never its column list).
 *
 * Search results, Customer-360 and the renewal/churn/trial worklists BADGE a retired account; they never
 * hide one. Those are surfaces a human explicitly asked for — the CustomersPage is search-first, with no
 * "all customers" list, so search is the ONLY path to some accounts. Filter it and the operator concludes
 * the portal is broken. Retirement is information, not noise. (Only the pure sell-more lists hide them,
 * and that filter lives in the DB view, not here.)
 */
export interface RetirementFlags {
  /** false = Zoom no longer LISTS this account. Absent on a payload/role that doesn't carry it. */
  is_active?: boolean;
  /** The day Zoom stopped listing them; null while active. */
  left_list_at?: string | null;
}

export interface SearchMember extends RetirementFlags {
  account_id: string;
  email: string;
  user_status: string | null;
  account_name: string | null;
  account_number: string | null;
}

/**
 * The state of the account's BASE PLAN (API migrations 0020 + 0021) — a state, deliberately NOT a verdict.
 *
 * `paid_expired` is the load-bearing one: Zoom says the plan expired, but the account HAS paid us real
 * money, and Zoom's `status` is a CLAIM that is sometimes wrong (QUALICA — four annual renewals, an open
 * Zoom support case, flipped active→expired overnight). The UI must escalate those to a human, never
 * render them as dead. `unknown` (no base-plan row / NULL status — 892 ordinary Basic accounts) is NOT
 * `expired` and must never be treated as one.
 *
 * ⚠️ `workaround_trial` (0021) IS THE ONE THAT LOOKS FINE AND IS NOT. It is an account that PAID US, whose
 * plan Zoom flipped to expired anyway, and which OUR OWN TEAM then patched onto a temporary plan so the
 * customer could keep working while Zoom is chased. Its plan reads `active`, its seats are current, and
 * every naive surface renders a perfectly healthy customer — Princess Galyani Vadhana Institute of Music
 * ($10,364.55, renewed, expired by a Zoom fault) was sitting on `v_trial_expiring` as a CONVERSION
 * PROSPECT. It is not a healthy account and it is not a prospect: it is an OPEN INCIDENT wearing a trial
 * coat, and the Zoom fault behind it is still open.
 *
 * Behaviour lives in lib/planState.ts — do not re-derive it at a call site.
 */
export type PlanState =
  | "live"
  | "lapsed_trial"
  | "paid_expired"
  | "expired_other"
  | "cancelled"
  | "workaround_trial"
  | "unknown";

/**
 * The account status badge as a SERVER VERDICT (API migration 0030, `v_account_status`, the ruling of
 * 2026-09-17) — FREE if `sub_accounts.account_type LIKE 'Free (%'`, ACTIVE if any subscription line is
 * still valid, EXPIRED otherwise. See lib/accountStatus.ts::accountStatusOf — a present value from this
 * whitelist WINS outright over `plan_state` + `base_type` below, which the same function now treats as the
 * FALLBACK (superseded, not deleted): an old/stripped payload that omits `plan_badge` still renders
 * correctly through the pre-0030 collapse.
 *
 * ⚠️ 2026-09-18 (round 3, ruling 3): `unknown` is now a FOURTH, genuinely server-side value — SETTLED, not
 * an abstention this frontend invented. It means "not Free AND zero renderable subscription lines" (a
 * 4-account cohort on the local mirror), which is a DIFFERENT fact from `PlanState`'s own `unknown` (an
 * opaque plan code the fallback collapse could not classify — see accountStatusOf's switch). Both surface
 * as the same word ("Unknown") but carry DIFFERENT hints — see AccountStatusBadge.
 */
export type PlanBadge = "active" | "free" | "expired" | "unknown";

/**
 * plan_state as it rides along on a BADGE-ONLY surface (search, worklist rows). Optional because the
 * server LEFT JOINs it and older/stripped payloads simply omit it — absent is read as `unknown`, never
 * as expired. See lib/planState.ts::planStateOf.
 */
export interface PlanStateFlags {
  /** The real plan SKU (v_portal_account_360.base_type). The truth; `account_type` is the decoy. */
  base_type?: string | null;
  /** Absent/null → `unknown`. NEVER infer "expired" from a missing value. */
  plan_state?: PlanState | null;
  /** The 0030 ruling's verdict. Absent on an old/stripped payload → the badge falls back to plan_state +
   *  base_type (see the PlanBadge doc above). NEVER re-derive this client-side. */
  plan_badge?: PlanBadge | null;
}

export interface SearchAccount extends RetirementFlags, PlanStateFlags {
  account_id: string;
  account_number: string | null;
  account_name: string | null;
  owner_email: string | null;
  /** Zoom's raw account type. A DECOY for entitlement — never the plan label (use base_type, which search
   *  also returns) and never used in logic. Display-only where rendered at all (the Customer-360 header's
   *  Account type reads CustomerOverview.account_type_display, not this). */
  account_type: string | null;
}

/**
 * Whether an invoice search hit can be drilled into a real customer (API K2-A, server-derived —
 * never re-compute it client-side):
 *   attributed      — account_id is a real customer; drill to /customers/{id}?tab=invoice
 *   master_account  — belongs to the reseller's own account (e.g. 941855), not a customer
 *   unattributed    — attribution pending or permanently unresolved (multi-account mix, unknown
 *                      sub-account number, ...)
 * Optional because an older/stripped payload may omit it — absent is treated the same as
 * `account_id == null` (see SearchResults.tsx), never assumed attributed.
 */
export type InvoiceAttributionState = "attributed" | "master_account" | "unattributed";

export interface SearchInvoice {
  invoice_id: string;
  invoice_number: string;
  /** Null when the invoice has no known customer yet (or ever) — see attribution_state. Never
   *  build a drill path from a null account_id (that would navigate to /customers/null). */
  account_id: string | null;
  invoice_date: string | null;
  attribution_state?: InvoiceAttributionState | null;
}

/**
 * An email Zoom lists ONLY as a pending invitee of this account (id-less, status=pending — invited,
 * not accepted yet), so it is not a member and the `members` arm cannot find it (API wave 7). Opens the
 * account's Users → Pending tab. The server omits a hit when the same account's roster already lists
 * the email, or when that account's pending list is not loaded for its roster day.
 */
export interface SearchPending extends RetirementFlags {
  account_id: string;
  /** Verbatim as Zoom sent it. */
  email: string;
  account_name: string | null;
  account_number: string | null;
  // No user_type / invited_at: /search is open to every role (search:read), while the invitee's
  // licence and invite time belong to the Users tab behind customer:read.
}

export interface SearchResult {
  query: string;
  members: SearchMember[];
  accounts: SearchAccount[];
  invoices: SearchInvoice[];
  /** Absent on an API that predates wave 7 — read as []. */
  pending?: SearchPending[];
}

// ---------------------------------------------------------------------------------------------------
// Customer-360 (GET /api/v1/customers/{id} + tabs)
// ---------------------------------------------------------------------------------------------------

/**
 * Overview KPIs. base_type is the REAL plan label; account_type (if present) is a DECOY. Money fields
 * (billed_12m, renewal_amount, renewal_currency) are ABSENT for non-money roles.
 */
export interface CustomerOverview {
  account_id: string;
  account_name: string | null;
  account_number: string | null;
  owner_email: string | null;
  /** The real plan label — use THIS, not account_type. */
  base_type: string | null;
  /** Human plan tier (bundle_tier_map). "UNKNOWN" when unmapped; tier_known=false → render "Tier unknown". */
  tier?: string | null;
  tier_known?: boolean;
  bundled_services?: string[];
  /** Zoom's raw account type (sub_accounts.account_type). A DECOY for entitlement (0013 + 0020): rendered
   *  for DISPLAY ONLY — via account_type_display below — and never used in logic. base_type is the plan. */
  account_type?: string | null;
  /** N0-4 · "Account type": account_type, except 'Business' on an Enterprise-family tier reads
   *  'Enterprise' (what Zoom's Customer List shows). Rendered on the header AND, since the 2026-09-18
   *  owner ruling, on the Overview card in place of the plan name — one rendering rule, both fed by this
   *  same field. Display only — branch on nothing here. Absent on an older API → neither surface shows an
   *  Account type fact. */
  account_type_display?: string | null;
  /** True when the account has NO meeting plan (Zoom Phone only; the PLANCODE_7200/7201 placeholder base).
   *  The header then drops Seats / Seats (last known): that figure is a 0 default, not a size. */
  no_meeting_plan?: boolean;
  /**
   * Purchased seats — and NULL once the plan is provably not live (0020). The backend moves the frozen
   * figure to `seats_last_known` rather than leaving a leftover trial number under a live-sounding name:
   * ISALAMIC's dead zoent_freetrial still reads 15 on the account row. Render "—", not a phantom.
   */
  seats: number | null;
  /** The frozen seat count, NAMED for what it is. Only present when plan_state is not-live. */
  seats_last_known?: number | null;
  /** State of the base plan. Absent → `unknown`. See PlanState + lib/planState.ts. */
  plan_state?: PlanState | null;
  /** Zoom's raw claim ('expired' | 'active' | 'cancelled' | …). A CLAIM, not a fact. */
  plan_status?: string | null;
  /** When Zoom last refreshed this plan row, and how stale that is. */
  plan_status_at?: string | null;
  /**
   * ⚠️ UNREAD BY THE FE SINCE THE COPY CUT. Kept on the type as the backend contract, NOT as live signal.
   *
   * Its only consumer was the freeze notice's age trailer ("Zoom last refreshed this plan {N} days ago"),
   * which was deleted: on the real prod rows ZPCD re-fetches the dead plan row every day, so N is 0, and
   * the panel rendered "…these numbers will never update again. Zoom last refreshed this plan 0 days ago"
   * — a notice arguing with itself. The backend still sends the field and the fixtures still carry it;
   * nothing renders it. Same situation as `plan_incident_note` below, and it gets the same treatment: say
   * so out loud, so the next reader does not mistake a dead field for a live one and wire a decision to it.
   */
  plan_data_age_days?: number | null;
  /** The day the status flipped — only meaningful when plan_flip_seen is true. */
  plan_status_since?: string | null;
  /**
   * TRUE only when we actually WITNESSED the transition. An account that was already expired for the
   * whole of our retained history reads `false` — the backend refuses to invent a flip date out of a
   * data-retention boundary, and neither may the UI.
   */
  plan_flip_seen?: boolean | null;
  /** Server-composed sentence for the freeze notice; null when the plan is live/unknown → no banner. */
  plan_freeze_note?: string | null;
  /**
   * The 0030 ruling's verdict (see PlanBadge). Absent on an old/stripped payload → the three badge
   * surfaces fall back to `plan_state` + `base_type` above, byte-identical to the pre-0030 collapse.
   * ⚠️ Present + `free` can legitimately DISAGREE with `plan_state`/`seats_last_known`/`renewal_date`
   * below (QUALICA, G STEEL, GJ Steel: a real historical base plan, but "Free (with credit card)" on
   * `account_type` beats it under the ruling) — those now-superseded figures are what CustomerHeader's
   * zombie-row suppression exists to hide. Reconciling plan_state/plan_freeze_note themselves is an OPEN
   * product question and out of scope here (see the header's comment; do not touch PlanFreezeNotice).
   */
  plan_badge?: PlanBadge | null;
  /**
   * ⚠️ `plan_incident_note` IS DELETED FROM THIS TYPE — and the API contract is NOT broken by that.
   *
   * The backend (API 0021) still sends the field on a `workaround_trial` account. Nothing here reads it
   * any more: the Customer-360 incident banner it fed was removed outright on the principal's instruction
   * ("แจ้งเตือนรกเกินไป"), and the badge that replaced it composes its own localized copy from
   * `plan_state`, exactly as it always did — the field was never the source of what rendered. An extra key
   * in the JSON is simply ignored, so no request or response shape changes and no backend change is
   * required or implied.
   *
   * It was optional (`?`) and unread even before this, kept "so a future non-i18n surface could use the
   * server's sentence". That surface does not exist, and a type field kept for a hypothetical reader is
   * how dead plumbing survives a cleanup. Should an export or an email ever want the server's sentence,
   * add it back at that point — against a real consumer.
   *
   * The account's incident status remains fully readable from `plan_state === "workaround_trial"`.
   */
  account_status: string | null;
  utilization_band: string | null;
  plan_util_pct: number | null;
  meetings_30d: number | null;
  renewal_date: string | null;
  days_until_renewal: number | null;
  last_synced_at: string | null;
  /** Retirement flags (sub_accounts base table). Customer-360 BADGES a retired account, never hides one:
   *  someone navigated here on purpose, and "left Zoom's list on <date>" is then the most important fact
   *  on the page. */
  is_active?: boolean;
  left_list_at?: string | null;
  // --- MONEY (absent for non-money roles) ---
  billed_12m?: number;
  renewal_amount?: number;
  renewal_currency?: string;
  // The view is recreated from ZPCD at runtime, so extra columns can appear; tolerate them.
  [key: string]: unknown;
}

export interface SubscriptionRow {
  account_id: string;
  status: string | null;
  service_effective_date: string | null;
  next_invoice_date: string | null;
  // --- MONEY (absent for non-money roles) ---
  amount?: number;
  currency?: string;
  [key: string]: unknown;
}

/** Bundled = the add-on's service is folded into the account tier; Paid = it is billed separately;
 *  "unknown" = the account's tier is not mapped (never assert bundled/paid). Null on the base row. */
export type BundledState = "bundled" | "paid" | "unknown" | null;

export interface AddonRow {
  plan_category: string | null;
  plan_type: string | null;
  hosts: number | null;
  status: string | null;
  service_effective_date: string | null;
  next_invoice_date: string | null;
  /** Bundled vs Paid vs graceful "unknown" (tier unknown → never assert). Null on the base row. */
  bundled?: BundledState;
  /** A line with a FIXED END (every free trial) rather than a renewal. Zoom prints these as
   *  "<start> (expires on <end>)" in the Service Effective cell and leaves Next Invoice Date EMPTY —
   *  nothing will ever be invoiced. Absent on an older API → treated as a recurring line. */
  is_fixed_end?: boolean;
  /** The fixed end itself (ISO date). Only meaningful when is_fixed_end. */
  expires_on?: string | null;
  /** T2 · a calling / add-on line spliced in under the Zoom Phone shell — Zoom prints it as a child
   *  line ("--Zoom Phone Pro"). Absent on every other line, and on an older API. */
  is_child_line?: boolean;
}

export interface CustomerSubscription {
  /** Zoom shows this account as "Basic Plan (Free)" — a card, never a plan table. Decided from the
   *  account's current type, because plan rows outlive the plan in the mirror. */
  is_free?: boolean;
  subscription: SubscriptionRow | null;
  base_type: string | null;
  /** The account's plan tier (bundle_tier_map). "UNKNOWN"/tier_known=false → render "Tier unknown". */
  tier?: string | null;
  tier_known?: boolean;
  bundled_services?: string[];
  addons: AddonRow[];
  note: string | null;
  /** When the plan/usage mirror for this account was last refreshed (nightly or a Sync-now). Drives
   *  the grey "Last synced …" caption next to the Sync-now button. Null if never synced. */
  plan_synced_at: string | null;
}

/** The Usage tab's BASE row (N0-3): the base/bundle LINE the Subscription tab lists — labelled from its
 *  own plan_type (the SKU, resolved with planName exactly as the Subscription tab does) — with the
 *  plan-usage figures the Subscription tab's quantity also reads. null figures = no plan-usage data
 *  (e.g. an expired trial Zoom no longer reports on) → "—". Never the PLANCODE_720x placeholder. */
export interface PlanUsageRow {
  plan_category: string | null;
  plan_type: string | null;
  status: string | null;
  hosts_used: number | null;
  hosts_total: number | null;
  utilization_pct: number | null;
}

export interface RecordingStorage {
  // NOTE: used/plan arrive as Decimal JSON strings ("2017.28"/"0.00") at runtime; coerced on use.
  used_storage_gb: number | null;
  plan_storage_gb: number | null;
  used_pct: number | null;
  /** Free/base-tier cap in GB, unit-corrected in the mirror (e.g. "21.98 TB" → 22507.52). Use THIS for the
   * cap math — never parse the free_storage label string (it drops the unit and reads TB as GB). */
  free_storage_gb: number | null;
  /** Base tier as a label: "10 GB" / "Unlimited". Total cap = free + plan; "Unlimited" → truly uncapped. */
  free_storage: string | null;
}

/** An add-on LINE on the Usage tab (N0-3) — exactly one of the Subscription tab's visible lines (bundled
 *  lines are dropped server-side), with its own figures. The FE renders these verbatim: no fallbacks. */
export interface UsageAddon {
  plan_category: string | null;
  plan_type: string | null;
  /** The line's own licence quantity (what the Subscription tab prints). null for the Zoom Phone
   *  Pay-As-You-Go shell, which has none. */
  hosts: number | null;
  status?: string | null;
  /** Assigned for THIS line, or null ("—"): Zoom Phone Pro = Zoom's calling-plan count (0 when phone is
   *  not enabled); webinar/large_meeting = per-user holders net of bundled, only when the category has
   *  ONE live line; everything else null. Never the per-user phone flag count. */
  assigned: number | null;
  /** Capacity = the line's OWN quantity (equal to `hosts`); null for the shell. Never a category sum. */
  assigned_capacity: number | null;
  /** Utilization of `assigned` vs this line's capacity; null for phone, for 0 assigned, or when holders
   *  exceed the seats (a >100% bar would be absurd). */
  assigned_util_pct: number | null;
  /** True for Zoom Phone lines — no utilization bar. */
  is_payg: boolean;
  /** Whether the account has ANY per-user license row — guards "0 assigned" vs "no per-user data". */
  license_coverage: boolean;
}

export interface CustomerUsage {
  /** A1: Zoom shows the Basic Plan (Free) card and no plan table — the Usage tab does the same. */
  is_free?: boolean;
  /** Zoom answered 403/2031 for this account's phone calls — the tab says so under the table. */
  phone_not_enabled?: boolean;
  /** The base row (0 or 1). */
  plan_usage: PlanUsageRow[];
  recording_storage: RecordingStorage | null;
  /** False = the account has no per-user license data → add-on `assigned` shown as "—" (not "0"). */
  addon_license_coverage: boolean;
  /** Add-on plans (Phone, Webinar, …) with their licensed quantity + per-user assignment. */
  addons: UsageAddon[];
  /** The account's resolved base plan — read by the freeze notice's wording; no longer a row label. */
  base_type: string | null;
  /** When plan/usage was last pulled from Zoom (max fetched_at of sub_account_plans) — SAME source as
   * the Subscription tab's "Last synced" caption. null = never synced. */
  synced_at: string | null;
  /** Staleness note for the subscription-sourced add-on rows (a different feed cadence than `synced_at`)
   * — mirrors the Subscription tab's note so the caption never reads fresh over stale add-ons. null when
   * the subscription feed is current (or the account has no subscription rows). */
  note: string | null;
  /**
   * ── The plan-state freeze (0020) ─────────────────────────────────────────────────────────────────
   * When the base plan is not live, Zoom's /plans/usage endpoint returns NOTHING for the account, so
   * every figure above — hosts_total/hosts_used, the add-on rows, the utilization bands — is frozen at
   * the moment the trial or the paid term ended and will never refresh. They must NOT be rendered as
   * current: ISALAMIC shows "15 purchased / 1 used" on a plan that died on 2026-05-28.
   *
   * DELIBERATELY SEPARATE from `note` above. That one reports subscription-FEED staleness — a transient
   * ZPCD problem that fixes itself. This reports a PERMANENT fact about the account: the plan is dead and
   * the endpoint is silent by design. Collapsing them would let a repaired feed silently erase the plan
   * warning, and would make a self-healing condition look identical to an irreversible one.
   */
  plan_state?: PlanState | null;
  plan_status_at?: string | null;
  /** ⚠️ Unread by the FE since the copy cut — the backend contract, not live signal. See CustomerOverview. */
  plan_data_age_days?: number | null;
  /** Ready-made sentence; null when the plan is live or unknown → the FE renders no banner. */
  plan_freeze_note?: string | null;
  /**
   * The 0030 ruling's verdict (see PlanBadge) — carried here too (round 3, ruling 2) so PlanFreezeNotice
   * can tell an Active-badged-but-not-live account (≈39 on the local mirror: Zoom-Phone-only base codes,
   * and a few whose fixed-end base is still in date while `plan_state` disagrees) from a genuinely dead
   * one, and reword its notice instead of suppressing it. Absent on an old/stripped payload → the notice
   * renders exactly as it always did, from `plan_state` alone. Confirmed present on the real API payload
   * (service.py's `customer_usage`, which sets it from the same `_account_status()` the Overview tab uses)
   * before this field was added here — the type had fallen behind the wire contract.
   */
  plan_badge?: PlanBadge | null;
}

// --- Engagement trend (GET /api/v1/customers/{id}/trend?window=30|90) — non-money -------------------

export type TrendWindow = 30 | 90;

export interface TrendPoint {
  date: string;
  meeting_count: number;
  webinar_count: number;
  participants: number;
  minutes: number;
  recording_meetings: number;
  screen_share_meetings: number;
  video_meetings: number;
}

export interface TrendTotals {
  meeting_count: number;
  webinar_count: number;
  participants: number;
  minutes: number;
  recording_meetings: number;
  screen_share_meetings: number;
  video_meetings: number;
}

export interface CustomerTrend {
  window_days: number;
  start: string;
  end: string;
  /** Coverage guard — false when the account has no metrics rows in the window (show empty-state). */
  has_data: boolean;
  series: TrendPoint[];
  totals: TrendTotals;
  /** Meeting feature adoption over meetings (never the merged total). Each null when there are no meetings. */
  adoption: {
    recording_pct: number | null;
    screen_share_pct: number | null;
    video_pct: number | null;
  };
}

export interface CustomerUser {
  email: string;
  user_status: string | null;
  /** Zoom user type → the base meeting license: 1=Basic, 2=Licensed, 4=None. */
  user_type: number | null;
  /** Zoom role id: 0=Owner, 1=Admin, 2=Member; anything else = a Custom role (id not resolvable here). */
  role_id: string | null;
  last_login_time: string | null;
  /** Zoom login methods (1=email, 100=Google, 101=Facebook, 27=SSO/Microsoft, …); for the row icon. */
  login_types: number[] | null;
  /**
   * The zoom_phone user-settings flag (falling back to a roster phone number). NOT the phone chip's source
   * of truth: used only by the pre-ruling fallback gate when `phone_plans` is null/absent.
   */
  has_phone: boolean;
  // --- Per-user license add-ons (v_latest_user_license). null = license view absent (pre-first-poll /
  //     a CI DB): treat null as "unknown", never assert the add-on is off. ---
  zoom_phone: boolean | null;
  webinar: boolean | null;
  webinar_capacity: number | null;
  large_meeting: boolean | null;
  large_meeting_capacity: number | null;
  zoom_events: boolean | null;
  meeting_capacity: number | null;
  // --- Bundled-awareness (v_latest_user_license.*_bundled). TRUE = the add-on is folded into the
  //     account tier → HIDE this per-user line. FAIL-SAFE: null/false/undefined (unknown tier carries
  //     false) MUST still show — only an explicit true suppresses, never hide a genuine à-la-carte seat. ---
  webinar_bundled?: boolean | null;
  phone_bundled?: boolean | null;
  large_meeting_bundled?: boolean | null;
  // --- N0-1 line gate (API customer_users). TRUE = the account holds a line of this add-on that the
  //     Subscription tab renders as Active/Cancelled. The per-user flags above are Zoom user SETTINGS that
  //     outlive (or never had) a purchase, so a chip needs this too. Only an explicit true shows a chip. ---
  phone_has_line?: boolean;
  webinar_has_line?: boolean;
  large_meeting_has_line?: boolean;
  /** Name parts live in raw_data jsonb on the server; null when absent. */
  first_name: string | null;
  last_name: string | null;
  display_name: string | null;
  /**
   * The user's phone chips from Zoom's calling plans (owner ruling 2026-09-18), decided by the API:
   * an ARRAY = authoritative (the account's phone fetch is ok / phone_not_enabled) — show exactly one chip
   * per entry, labelled with Zoom's plan name verbatim; [] = no phone chip (Workplace-bundle plans are
   * already removed). null / absent = the account has no phone data today, OR this one user's plans are
   * unknown (Zoom refused the single GET) → the pre-ruling gate (has_phone && live line && !bundled) decides.
   */
  phone_plans?: CustomerUserPhonePlan[] | null;
}

/** One calling plan a user holds (Zoom's `calling_plans[]` entry), as the Users-tab phone chip. */
export interface CustomerUserPhonePlan {
  type: number | null;
  /** Zoom's plan name, verbatim ("Zoom Phone Pro"). null → the generic "Zoom Phone" label. */
  name: string | null;
}

/**
 * A PENDING invitee (N0-6): Zoom lists them under status=pending with no user id. Invited to this
 * account, not accepted yet. Deliberately no role (Zoom sends role_id '0' — Owner — for every invitee)
 * and no "Currently in" account (removed, owner ruling 2026-09-19).
 */
export interface CustomerPendingUser {
  email: string;
  /** The licence the invite carries: 1 = Basic, 2 = Licensed. */
  user_type: number | null;
  /** When Zoom created the invite (user_created_at). */
  invited_at: string | null;
}

export interface CustomerUsers {
  users: CustomerUser[];
  /** TOTAL ACTIVE users on the account (a SQL aggregate, NOT users.length — the rows array is capped).
   *  Pending invitees are never included (they are `pending`). */
  count: number;
  /** How many user rows were actually returned (capped). */
  returned: number;
  /** True when count > returned (the rows array is a truncated slice of a large account). */
  truncated: boolean;
  /** The account's real base plan — a Licensed (type 2) user's License resolves to this. */
  base_type: string | null;
  /** When the users mirror was last refreshed (nightly or a Sync-users). Optional — the backend
   *  customer_users payload does not currently include it, so the caption simply hides when absent. */
  users_synced_at?: string | null;
  /** The phone mirror's verdict for the account: "ok" | "phone_not_enabled" | "error" | null (no data). */
  phone_fetch_status?: string | null;
  /** Pending invitees (N0-6), newest invite first — NEVER counted in `count`. Absent on an older API. */
  pending?: CustomerPendingUser[];
  /** null when the pending list is NOT LOADED for the roster's day (see pending_loaded) — never 0. */
  pending_count?: number | null;
  /** False until the portal holds the pending list for this account's roster day (before ZPCD's first
   *  pending partition, or while a day's attach lags): `pending` is then [] and means nothing. */
  pending_loaded?: boolean;
  /** The ZPCD day the pending list is from (ISO date), or null when none is loaded. */
  pending_snapshot_date?: string | null;
}

/**
 * Invoice tab — money-only. Viewer gets HTTP 403; the UI hides the tab when !sees_money. The list is the
 * UNIFIED history (source='api' recent + source='pdf' archive). has_pdf = a stored PDF path exists (the
 * file is streamed by the pdf endpoint, which 404s if the file isn't present in this environment).
 */
export interface InvoiceRow {
  invoice_id: string;
  invoice_number: string;
  invoice_date: string | null;
  due_date: string | null;
  /** Postgres NUMERIC → JSON string on the wire (e.g. "12.74"); null on ~7% of rows. formatMoney coerces. */
  total_amount: number | string | null;
  /** Often null on the real feed — never fabricate a symbol when absent. */
  currency: string | null;
  status: string | null;
  source: string | null;
  has_pdf: boolean;
  /** First non-empty PO number from the invoice's line items (for the in-tab filter); null if none. */
  po_number: string | null;
}

export interface CustomerInvoices {
  invoices: InvoiceRow[];
  count: number;
}

/** One line item on an invoice. Money/numeric fields arrive as JSON strings (Decimal); coerce on use. */
export interface InvoiceItem {
  charge_name: string | null;
  charge_type: string | null;
  partner_sku: string | null;
  quantity: number | string | null;
  unit_price: number | string | null;
  total_amount: number | string | null;
  tax_amount: number | string | null;
  start_date: string | null;
  end_date: string | null;
  is_credit: boolean | null;
}

/** GET /api/v1/customers/{id}/invoices/{invoice_number} — header + line items. Money-gated. */
export interface InvoiceDetail {
  invoice: InvoiceRow & {
    balance: number | string | null;
    tax_amount: number | string | null;
    account_number_raw: string | null;
  };
  items: InvoiceItem[];
}

// ---------------------------------------------------------------------------------------------------
// Reports (GET /api/v1/customers/{id}/reports?start&end&type) — non-money usage analytics
// ---------------------------------------------------------------------------------------------------

export type ReportType = "both" | "meetings" | "webinars";

export interface ReportMeta {
  account_id: string;
  account_name: string | null;
  account_number: string | null;
  /** Effective range after the server applied defaults + the 12-month floor. */
  start: string;
  end: string;
  days: number;
  type: ReportType;
  /** Trend/heatmap granularity the server chose: "day" (short range) or "month" (long). */
  bucket: "day" | "month";
  /** % of KPI events represented in top_users (history is backfilled less than metrics) — null if no data. */
  top_users_event_coverage: number | null;
  /** Host-history coverage behind Active hosts. `from` = first Bangkok day the history tables cover
   *  (queried server-side; null = no history at all). `partial` = the range starts before `from` and
   *  ends on/after it, so the Active hosts KPI covers `from`–end only. */
  active_hosts_coverage: { from: string | null; partial: boolean };
}

export interface ReportKpis {
  total_meetings: number;
  total_webinars: number;
  total_events: number;
  total_participants: number;
  total_minutes: number;
  total_hours: number;
  /** DISTINCT hosts (meeting/webinar per the Type filter, licensed or not) over the WHOLE range — never
   *  the sum of the per-bucket bars. null = the range lies wholly before host-history coverage. */
  active_hosts: number | null;
}

export interface ReportTrendBucket {
  /** ISO date ("YYYY-MM-DD") for day buckets, or "YYYY-MM" for month buckets. Every bucket between
   *  start and end is present (the axis is never compressed). */
  bucket: string;
  /** null = the bucket lies wholly before the daily-metrics coverage floor ("no data", not 0). */
  events: number | null;
  participants: number | null;
  minutes: number | null;
  /** Distinct hosts within this bucket; null = wholly before host-history coverage. */
  active_hosts: number | null;
  /** The bucket straddles the history floor, or its history holds < 95% of its metrics events. */
  active_hosts_partial: boolean;
}

export interface ReportHeatmapCell {
  date: string;
  events: number;
  minutes: number;
}

export interface ReportTopUser {
  host_email: string;
  host_display_name: string | null;
  meetings: number;
  webinars: number;
  events: number;
  participants: number;
  minutes: number;
}

/** One period of Zoom Phone call activity — the SAME buckets as `trend` (every period, in order). */
export interface ReportPhoneBucket {
  bucket: string;
  /** null = the period lies wholly before the phone feed's coverage floor ("no data", not 0). */
  call_count: number | null;
  inbound_count: number | null;
  outbound_count: number | null;
  /** The period starts before the floor: its counts cover part of it only. */
  partial: boolean;
}

export interface ReportPhoneKpis {
  total_calls: number;
  inbound: number;
  outbound: number;
  /** Call minutes = the range's call seconds / 60, rounded. */
  total_minutes: number;
  active_days: number;
}

/**
 * The Standard report's "Zoom Phone" section (wave 5, owner ruling 2026-09-19: phone call activity moved
 * here from the Usage tab). has_phone_data false = the account has no phone-metrics row at all → the
 * section is hidden (never a false "0 calls"). `kpis` null = the range lies wholly before coverage.
 */
export type ReportPhone =
  | { has_phone_data: false }
  | {
      has_phone_data: true;
      /** `from` = the phone feed's first day (queried; null = none); `partial` = the range straddles it. */
      coverage: { from: string | null; partial: boolean };
      kpis: ReportPhoneKpis | null;
      trend: ReportPhoneBucket[];
    };

export interface CustomerReport {
  meta: ReportMeta;
  kpis: ReportKpis;
  trend: ReportTrendBucket[];
  heatmap: ReportHeatmapCell[];
  top_users: ReportTopUser[];
  /** Absent from an API older than wave 5 → the section is simply not shown. */
  phone?: ReportPhone;
}

export interface ReportParams {
  start?: string;
  end?: string;
  type?: ReportType;
}

// ---------------------------------------------------------------------------------------------------
// Reports v2 — "Insights" (GET /api/v1/customers/{id}/reports/insights?start&end&type)
//
// The SECOND report on the Reports tab, alongside CustomerReport ("Standard") above. Deliberately
// different axes — license seat utilization, meeting/webinar SIZE, weekly RHYTHM, phone activity — and
// deliberately carries no `minutes` field anywhere: account_meeting_metrics_daily.duration_minutes_total
// is corrupted for dates >= 2026-06-10 (a participant-minutes/wall-clock-minutes mixup upstream, not yet
// backfilled), so this report avoids the concept entirely rather than surface a bad number.
// ---------------------------------------------------------------------------------------------------

export interface InsightsMeta {
  account_id: string;
  account_name: string | null;
  account_number: string | null;
  start: string;
  end: string;
  days: number;
  type: ReportType;
  bucket: "day" | "month";
  /** % of the headline daily-metrics event count that the size/weekly sections (both counted off the
   *  per-event HISTORY tables) actually cover — same caveat as CustomerReport's top_users coverage. */
  history_event_coverage_pct: number | null;
}

/** One snapshot-bucket of the primary plan's seat utilization (point-in-time, never summed). */
export interface LicenseTrendBucket {
  bucket: string;
  hosts_total: number | null;
  hosts_used: number | null;
  utilization_pct: number | null;
}

/** The latest sub_account_plan_usage snapshot (as of the report's own `end` date) for one plan_type. */
export interface LicenseLatestRow {
  plan_type: string | null;
  hosts_total: number | null;
  hosts_used: number | null;
  utilization_pct: number | null;
  snapshot_at: string | null;
}

export interface LicenseUtilization {
  /** The plan_type with the largest purchased hosts_total as of `end` — usually the base plan. Null
   *  when the account has no plan-usage snapshot at all as of that date. */
  primary_plan_type: string | null;
  /** Utilization trend for `primary_plan_type` only, bucketed like the Standard report's own trend. */
  trend: LicenseTrendBucket[];
  /** Latest snapshot per plan_type (base + add-ons) — context only; the FE highlights the primary. */
  latest_by_plan: LicenseLatestRow[];
}

export interface SizeDistributionBucket {
  /** "1" | "2" | "3-5" | "6-10" | "11-25" | "26-50" | "51-100" | "100+". */
  label: string;
  count: number;
  participants: number;
}

export interface SizeDistribution {
  buckets: SizeDistributionBucket[];
  /** Events with a null participant_count — tracked separately, never folded into a size bucket. */
  unknown_count: number;
  total_events: number;
  /** Mean attendees across the COUNTED (non-unknown) events; null when there are none. */
  avg_participants: number | null;
}

export interface WeeklyPatternDay {
  /** ISO weekday, 1=Monday..7=Sunday. */
  weekday: number;
  count: number;
}

export interface WeeklyPatternHour {
  /** 0-23, Asia/Bangkok local hour. */
  hour: number;
  count: number;
}

export interface WeeklyPattern {
  by_weekday: WeeklyPatternDay[];
  by_hour: WeeklyPatternHour[];
}

export interface CustomerReportInsights {
  meta: InsightsMeta;
  license_utilization: LicenseUtilization;
  size_distribution: SizeDistribution;
  weekly_pattern: WeeklyPattern;
}

// ---------------------------------------------------------------------------------------------------
// Activation badge (GET /api/v1/customers/{id}/activation) — customer:read
//
// Spec: 1toall-portal-api docs/ACTIVATION-BADGE-ADR.md. The ONE signal is "the owner account has signed
// in" (Zoom has no activation field). The API sends three states + a tier; the badge shows three texts
// ("Activate: Activated" / "Activate: Pending" / "Activate: Unknown" — lib/activationStatus.ts). Never red,
// never amber, never a negative claim.
// ---------------------------------------------------------------------------------------------------

/** The API's three states. lib/activationStatus.ts maps state × tier exhaustively to the three display
 *  variants. */
export type ActivationState = "activated" | "needs_checking" | "no_data";

/** Which pipeline watches the account (by first_seen_at): hot ≤30 d (30 s poll) · warm 31-90 d (5 min)
 *  · roster >90 d (nightly roster only). Null when first_seen_at is unknown. */
export type ActivationTier = "hot" | "warm" | "roster";

/**
 * GET /customers/{id}/activation. EVERY field is optional on purpose (ADR §Frontend "activation?"): the
 * badge is its own endpoint, and an API image rolled back to before it — or a payload missing a field —
 * must degrade to `no_data` ("Activate: Unknown"), never blank the Customer-360 header. Normalise
 * through activationStateOf().
 */
export interface CustomerActivation {
  state?: ActivationState | undefined;
  /** The latch instant (poll) or the roster owner's sign-in (mirror). Null unless activated. */
  activated_at?: string | null | undefined;
  source?: "poll" | "mirror" | null | undefined;
  /** When the pipeline last looked at this account — the badge prints it. */
  checked_at?: string | null | undefined;
  /** The pipeline that drives this account has gone quiet → the FE demotes to neutral + "not updating". */
  stale?: boolean | undefined;
  owner_first_seen_at?: string | null | undefined;
  tier?: ActivationTier | null | undefined;
}

// ---------------------------------------------------------------------------------------------------
// Worklists (GET /api/v1/worklists/{kind}) — Owner/Admin (worklist:read)
// ---------------------------------------------------------------------------------------------------

/** The four generic worklists (ZPCD views) that drive the dashboard health-band strip + generic tables.
 *  The add-on seat-waste list is a fifth, purpose-built list with its own typed shape (AddonWasteRow) —
 *  kept OUT of this union so the dashboard's exhaustive per-kind Record maps stay over just the four. */
/**
 * The generic worklists, in dashboard order. THE single source of truth: `WorklistKind` is derived from
 * this array, so a new kind is one edit here and every `Record<WorklistKind, …>` (icons, medallions,
 * tones, mock rows) fails to compile until it is filled in. A hand-written union let a duplicate list in
 * `src/mock/handlers.ts` fall out of step silently — an array literal missing a member is legal TypeScript.
 *
 * `trial_expiring` is a CONVERSION list, not a renewal one — Zoom omits trials from the renewals feed
 * that drives `renewal`, so these accounts appear nowhere else. Its `days_until_renewal` can be NEGATIVE
 * (a trial Zoom still reports active past its end date); WorklistTable renders those as overdue.
 *
 * `addon_waste` is deliberately absent: it has a purpose-built row shape and its own hook/table, so adding
 * it here would force it through WorklistTable, whose defensive pickString/pickNumber column probing would
 * render the wrong columns. (`retired_billable` and `plan_incident` were two more such kinds, but their FE
 * row types / hooks / tables were REMOVED on 2026-07-15 — the /worklists/{kind} endpoints still exist on
 * the API and are intentionally kept, there is simply no FE consumer of them now. Neither is a generic
 * kind either way.)
 */
export const WORKLIST_KINDS = [
  "renewal",
  "churn",
  "upsell",
  "underutilized",
  "trial_expiring",
] as const;

export type WorklistKind = (typeof WORKLIST_KINDS)[number];

/** Rows come straight from ZPCD views, so columns vary by kind; money columns are stripped by role. */
export type WorklistRow = Record<string, unknown>;

export interface Worklist {
  kind: WorklistKind;
  rows: WorklistRow[];
  count: number;
}

/**
 * GET /api/v1/worklists/addon_waste — purchased add-on seats minus per-user assigned, ranked by waste.
 * renewal_amount/renewal_currency are money → ABSENT for non-money roles (server strip). Phone is PAYG
 * (waste_seats null, is_payg true); an account with no per-user license row is coverage-guarded
 * (assigned_seats/waste_seats null, has_license_coverage false — NOT a false "all wasted").
 */
export interface AddonWasteRow {
  account_id: string;
  account_name: string | null;
  account_number: string | null;
  owner_email: string | null;
  /** 'webinar' | 'large_meeting' | 'phone'. */
  addon_category: string;
  is_payg: boolean;
  purchased_seats: number | null;
  assigned_seats: number | null;
  has_license_coverage: boolean;
  waste_seats: number | null;
  utilization_pct: number | null;
  next_invoice_date: string | null;
  // --- MONEY (absent for non-money roles) ---
  renewal_amount?: number;
  renewal_currency?: string;
}

export interface AddonWasteWorklist {
  kind: "addon_waste";
  rows: AddonWasteRow[];
  count: number;
}

/**
 * GET /api/v1/worklists/lapsed_trial — the PROSPECT list (API migration 0020, v_lapsed_trial, 50 rows).
 *
 * People who tried Zoom and did not buy. A FIRST-SALE motion, not a renewal one — so the row carries what
 * a salesperson actually dials (name, number, owner email, the trial SKU, the seats they trialled, when it
 * lapsed) and NO money columns at all: by definition nobody on this list has ever paid us a cent.
 *
 * This is the ONLY plan_state that is ever hidden from anything, and only from the three sell-more lists
 * (in SQL, in the leaf views). Selling MORE to someone who bought NOTHING is noise.
 */
export interface LapsedTrialRow {
  account_id: string;
  account_name: string | null;
  account_number: string | null;
  owner_email: string | null;
  base_type: string | null;
  /** The trial SKU (same value as base_type, e.g. `zoent_freetrial`). */
  plan: string | null;
  /** How big the trial was — the size of the opportunity, and the list's primary sort. */
  seats_trialled: number | null;
  /** The day the trial ran out (the base plan's own next_invoice_date). */
  lapsed_on: string | null;
  days_since_lapsed: number | null;
  plan_status_at: string | null;
  plan_data_age_days: number | null;
  plan_status_since: string | null;
  plan_flip_seen: boolean | null;
}

export interface LapsedTrialWorklist {
  kind: "lapsed_trial";
  rows: LapsedTrialRow[];
  count: number;
}

/**
 * GET /api/v1/worklists/summary — one round trip for every worklist COUNT plus the freshness of the
 * matview they are read from (ZPCD Portal Worklist Performance Refactor, 2026-07-24). Registered on the
 * server BEFORE `/worklists/{kind}` so the literal path segment "summary" is never captured as `kind`.
 *
 * `counts` is keyed by worklist kind (a superset of `WorklistKind` — the server derives it from its own
 * `WORKLIST_VIEWS` map, so it can carry kinds this FE build does not render a tile for; callers index with
 * `summary.counts[kind] ?? null`, never assume every key is present).
 *
 * `refreshed_at` is the last SUCCESSFUL refresh of `mv_account_360` (tz-aware ISO), NEVER the moment of
 * this request — a failed or skipped refresh leaves it at its prior value. `stale` is computed
 * server-side (NULL refreshed_at, >45 min old, or a recorded refresh error) so the client never has to
 * re-derive the threshold. This is a DISTINCT freshness fact from the `mirror`/`mirror-health` signals —
 * render it through `FreshnessBadge kind="worklist"`, never through the mirror badge (its own docstring
 * forbids reusing one timestamp for another kind).
 */
export interface WorklistSummary {
  counts: Record<string, number>;
  refreshed_at: string | null;
  stale: boolean;
}

// ---------------------------------------------------------------------------------------------------
// SKU pricing / cost reference (GET /api/v1/skus/pricing?months=) — money-gated (money:read only)
// ---------------------------------------------------------------------------------------------------

/** One partner_sku cost aggregate over the trailing window. total_cost/avg_per_line are money — the
 *  endpoint is money-gated (Viewers 403 before any DB touch), so a money role always has them. */
export interface SkuPricingRow {
  partner_sku: string;
  invoice_count: number;
  line_count: number;
  total_cost: number | null;
  avg_per_line: number | null;
  latest_invoice: string | null;
}

export interface SkuPricing {
  months: number;
  skus: SkuPricingRow[];
  count: number;
}

// ---------------------------------------------------------------------------------------------------
// Admin — portal user management (GET/PATCH /api/v1/admin/users) — Owner/Admin (user:manage)
// ---------------------------------------------------------------------------------------------------

/** A row in the portal-staff list (staff roster ⟗ app_user ⟗ auth_log last-seen). */
export interface AdminUser {
  /** app_user uuid — null for a roster-only invitee who has never signed in. */
  user_id: string | null;
  email: string;
  display_name: string | null;
  /** null only for a roster-only invitee (no app_user row yet). */
  role: Role | null;
  is_active: boolean | null;
  last_login_at: string | null;
  /** Most recent auth_log event (login/logout/failed) — distinct from last_login_at. */
  last_seen_at: string | null;
  provisioned: boolean;
  in_roster: boolean;
  /** Derived single status (service._derive_status). off_roster = has an account but pruned from the
   *  roster (the gate 403s). not_invited = on the roster but no invite sent yet (informational, not an
   *  error). revoked = an admin DELETED them: no app_user row AND a live tombstone. It ranks ABOVE
   *  not_invited on purpose — both have no account, but "we threw them out" and "nobody has invited
   *  them yet" are different facts, and only an explicit Invite clears the first. */
  status: "active" | "disabled" | "off_roster" | "invited" | "not_invited" | "revoked";
  /** Current Custom-role grants (empty for non-custom roles). Lets the role editor PRELOAD the existing
   *  set instead of silently replacing it on save. */
  custom_permissions: Permission[];
  /** When an operator last sent a portal invite to this roster member (null = never invited).
   *  DELETE clears it — the stamp lives on staff_zoom_roster and would otherwise outlive the account it
   *  describes, leaving the roster tab reading "Invited 3 days ago" for someone with no account. */
  invited_at: string | null;
  /** When an admin revoked portal access (the revoked_portal_access tombstone). null = never revoked. */
  revoked_at: string | null;
  /** Who pressed Delete (denormalised — survives that admin's own account being deleted). */
  revoked_by_email: string | null;
}

/** POST /api/v1/admin/users/invite */
export interface InviteBody {
  email: string;
}

export interface InviteResult {
  email: string;
  invited_at: string | null;
  /** False when SMTP is unconfigured or the send failed (the invite is still recorded). */
  email_sent: boolean;
  /** True when this invite also cleared a revocation tombstone — i.e. the operator just let a
   *  previously-DELETED person back in. Worth saying out loud; it is the only thing that clears it. */
  revoked_cleared: boolean;
}

/** DELETE /api/v1/admin/users/{id} — 200 only. Every refusal is a 4xx and throws ApiRequestError:
 *  404 no such user · 409 still active / self-delete / last active Owner · 403 an Admin deleting an Owner. */
export interface DeleteUserResult {
  deleted: true;
  user_id: string;
  email: string;
  role: Role;
  revoked_at: string | null;
}

/**
 * POST /api/v1/admin/users/sync-roster — "Sync users now".
 *
 * The server mirrors the outcome onto the HTTP status (ok→200, throttled→429, already_running→409,
 * zoom_error/empty→502, not_configured→503) but ALWAYS returns this envelope, so — exactly like
 * sync-now — the client does NOT throw on the non-2xx outcomes: the caller must render them. Rendering
 * on `res.ok` alone would collapse "the Zoom call failed" into a green tick.
 */
export const ROSTER_SYNC_STATUSES = [
  "ok",
  "throttled",
  "already_running",
  "zoom_error",
  "empty",
  // Zoom answered, but with so few staff that applying its list would have evicted a large slice of the
  // roster — which IS the sign-in allow-list, checked on every request. The server rolled the whole
  // rewrite back rather than trust a fetch that short. NOT a success, and not a small one either.
  "suspicious",
  "not_configured",
] as const;

export type RosterSyncStatus = (typeof ROSTER_SYNC_STATUSES)[number];

/** True only for a status this build knows how to render. Anything else is NOT a success — see
 *  RosterSyncResult.status. Derived FROM the array, never a hand-copied second list. */
export function isRosterSyncStatus(s: string): s is RosterSyncStatus {
  return (ROSTER_SYNC_STATUSES as readonly string[]).includes(s);
}

export interface RosterSyncResult {
  /**
   * The RAW server string — deliberately typed `string`, NOT narrowed to RosterSyncStatus.
   *
   * A TS union is not exhaustive-checked against server data: declaring this `RosterSyncStatus` would
   * let a status this build has never heard of arrive at runtime, sail through `tsc`, miss every branch
   * of the copy map and fall out of the UI as a blank panel — i.e. silently, as a non-failure. Typing it
   * `string` forces the caller through `isRosterSyncStatus()` and makes the unknown case a compile-time
   * obligation, which the panel discharges by treating it as a FAILURE.
   */
  status: string;
  /** Staff rows written. Non-zero only on `ok`. */
  synced: number;
  /** People this sync REMOVED from the sign-in allow-list (0 on every non-ok status — except
   *  `suspicious`, where it is how many the server REFUSED to remove). An eviction is an access change:
   *  a pruned staffer is 403'd on their very next click, mid-session. */
  pruned: number;
  /** The server's own statement that the previous roster still stands (true on EVERY non-ok outcome —
   *  a bad fetch never wipes the roster, which IS the auth allow-list). Render this, don't infer it. */
  roster_intact: boolean;
  /** Roster size read back AFTER the attempt — the truth, not an optimistic echo. */
  roster_size: number;
  /** max(staff_zoom_roster.synced_at) — the last SUCCESSFUL sync, present on every outcome. */
  last_synced_at: string | null;
  /** Seconds to wait, on `throttled` only. */
  retry_after_seconds: number | null;
  /** The server's English one-liner. We render our own localized copy; this is the fallback for an
   *  outcome this build does not know. */
  message: string;
  detail: string | null;
}

export interface AdminUsers {
  users: AdminUser[];
}

/** PATCH /api/v1/admin/users/{id}/role */
export interface RoleUpdateBody {
  role: Role;
  /** Required only when role === "custom"; validated ⊆ CUSTOM_GRANTABLE by the server. */
  custom_permissions?: Permission[];
}

/** PATCH /api/v1/admin/users/{id}/active */
export interface ActiveUpdateBody {
  is_active: boolean;
}

// ---------------------------------------------------------------------------------------------------
// Pool module (GET/POST/PATCH/DELETE /api/v1/pool/*) — Zoom license/period time-sharing.
//
// Every route is Bearer-gated like the rest of the portal, PLUS a server-side POOL ROLE that is
// COMPLETELY SEPARATE from the RBAC roles/permissions above — a portal Owner or Admin is not
// automatically a pool operator, and a portal Viewer might be one. A 403 from any of these endpoints
// therefore means "not enough pool role for this action", not "broken" and not "no RBAC permission" —
// screens render a dedicated not-an-operator state (see features/pool/PoolErrorState.tsx), never the
// generic RBAC-forbidden copy.
//
// rev 6 (2026-07-21, POOL-UI-REV6-SPEC.md): the allow-list model changed shape.
//   - `Role.OWNER` of the PORTAL always has full pool rights — IMPLICITLY, with no allow-list row at
//     all ("even the Owner must be listed" from the original design is gone). GET /access reports this
//     caller as pool role `"owner"` regardless of any `pool.operator` row.
//   - Everyone else's pool role comes from their OWN `pool.operator` row: `"admin"` (full read/write,
//     same ceiling as Owner except they cannot touch the Owner's own access) or `"viewer"` (read-only:
//     dashboard/accounts/users only — no settings, no system, no intents, no mutations). No row at all
//     → pool role `null` (not an operator, 403 from every /pool/* route below the never-403 /access).
//   - `_manage` (owner ∪ admin) gates everything except the three read surfaces; `_view` (owner ∪ admin
//     ∪ viewer) gates GET dashboard/accounts/users/customers/{email}/history/status. See PoolAccess.
// ---------------------------------------------------------------------------------------------------

/** This caller's pool role (see GET /access below) — `null` means "not an operator at all". */
export type PoolRole = "owner" | "admin" | "viewer" | null;

/** GET /api/v1/pool/accounts/candidates?q= — accounts eligible to become (or already are) a pool. */
export interface PoolAccountCandidate {
  account_id: string;
  account_number: string | null;
  account_name: string | null;
  account_type: string | null;
  seats: number | null;
  /** True when this account is already a pool — the Add button reflects this instead of re-adding. */
  already_pool: boolean;
}

export interface PoolAccountCandidates {
  accounts: PoolAccountCandidate[];
}

/**
 * GET /api/v1/pool/accounts — a configured pool account with its Zoom-mirrored usage figures.
 *
 * rev 9 (21/7, principal decision — managed-domain removal): `has_managed_domain` /
 * `managed_domains_checked_at` are GONE from this shape — the guardrail #6 unlink gate they fed is
 * removed entirely (see PeriodModal.tsx's own banner). Do not re-add either field without re-reading
 * that decision.
 */
export interface PoolAccount {
  account_id: string;
  account_number: string | null;
  account_name: string | null;
  under_sub_account: boolean;
  enabled: boolean;
  added_by: string;
  added_at: string;
  /**
   * rev 7 (21/7, from a live prod screenshot review): believed to be a REAL plan label from the ZPCD
   * mirror ("Pro" / "Education" / "Business" / …), added by the API alongside `plan_type`. Null for a
   * standalone (credential, R4) account — there is no mirror row to read from.
   *
   * rev 10 (22/7, principal decision): rev 7's belief was WRONG — verified on prod 21/7 that this field
   * actually carries the same raw `base_type`-style plan keys as everywhere else in the app (edu20 /
   * yearly / zoe_yearly / …), not a clean label. The Pool Accounts Account Type cell now routes it through
   * `tierOf()` (lib/planNames.ts) to get one of the four tiers the principal wants (Pro/Business/
   * Enterprise/Education). `plan_type` stays in the response either way (the BE change was purely
   * additive) but nothing reads it — see `plan_type`'s own comment below.
   *
   * ⚠️ DO NOT confuse this with `SearchAccount.account_type` / `CustomerOverview.account_type` elsewhere
   * in this file — those are documented DECOYS (use `base_type`). This IS the real plan key for this
   * endpoint (there is no separate `base_type` field here) — it is just raw, not a display label.
   */
  account_type: string | null;
  /** No longer displayed (rev 7) — see `account_type` above. Left on the type because the BE still sends
   *  it; nothing reads it now. */
  plan_type: string | null;
  hosts_total: number | null;
  hosts_used: number | null;
  /** Zoom's OWN subscription start/end claims (read-only) — distinct from any customer's Service
   *  Period. rev 6 R5: `subscription_start` reads `raw_data->>'subscription_start_time'` from the ZPCD
   *  mirror, paired with `subscription_end`. Both null for a standalone (credential, R4) account — there
   *  is no mirror row to read from; the Pool Accounts screen renders "—" for every mirror-derived column
   *  in that case (account_type/hosts_total/subscription_start/subscription_end). */
  subscription_start: string | null;
  subscription_end: string | null;
  /** rev 15.2 — the operator-entered Zoom subscription PERIOD (standalone accounts only; a
   *  per-account S2S app has no billing scope). Both null for master accounts and for standalone
   *  accounts not yet filled. `subscription_start`/`subscription_end` already COALESCE these in;
   *  these raw fields let the manage dialog pre-fill the range picker. */
  manual_subscription_start: string | null;
  manual_subscription_end: string | null;
  /** rev 16 — the operator-entered Account Type + Seats (standalone accounts only; a per-account
   *  S2S app has no mirror row to read either from). Both null for master accounts and for a
   *  standalone account not yet filled in. `account_type`/`hosts_total` above already COALESCE
   *  these in; these raw fields let the manage dialog pre-fill its inputs — same pattern as
   *  manual_subscription_start/_end. `account_type` is one of exactly four values when set: "Pro" /
   *  "Business" / "Enterprise" / "Education" (see tierOf() in lib/planNames.ts — chosen so this
   *  value round-trips through that bucketing unchanged, no special-casing needed on this screen). */
  manual_account_type: string | null;
  manual_seats: number | null;
  /** rev 10 (22/7): the "Members" column was removed entirely from Pool Accounts (superseded by
   *  Assigned + the new Unassigned column, both derived from hosts_total/licensed_members). Left on the
   *  type because the BE still sends it; nothing reads it now — same treatment as `plan_type` above. */
  members: number | null;
  licensed_members: number | null;
}

export interface PoolAccounts {
  accounts: PoolAccount[];
}

/** POST /api/v1/pool/accounts — 201; 409 already a pool; 404 not found in the ZPCD mirror. Also the
 *  response shape of POST /api/v1/pool/accounts/credential (rev 6 R4) — same ack either way. */
export interface PoolAddAccountResult {
  account_id: string;
}

/**
 * DELETE /api/v1/pool/accounts/{account_id} (rev 7, gate `_manage`) — removes the account from the pool.
 * The sweep stops watching it and its OBSERVED Zoom membership rows are dropped (they're just the
 * sweep's own cache of what it last saw). Customer service PERIODS and the audit history for this
 * account are a business record and are deliberately KEPT — this is a pool-membership removal, never a
 * data-destruction op. 404 = no such pool account.
 */
export interface PoolRemoveAccountResult {
  account_id: string;
  removed: boolean;
  /** จำนวนแถว membership (observed state) ที่ถูกลบตามบัญชี — โชว์ใน success toast */
  members_removed: number;
}

/**
 * POST /api/v1/pool/accounts/credential (rev 6 R4, gate `_manage`) — add a pool account STANDALONE, off
 * a Zoom S2S credential rather than an existing ZPCD-mirrored sub-account (the real case: a Zoom account
 * with real paying customers that never landed in the mirror). The server live-verifies the credential
 * against Zoom (an `account_credentials` token + one read call) BEFORE writing anything:
 *   - 503 — `POOL_CRED_KEY` is not configured on this box; the secret cannot be encrypted at rest.
 *   - 422 — the live verify failed (bad token / wrong scope / network) — server message rendered
 *     VERBATIM, same as every other refusal in this module.
 *   - 409 — this account_number/zoom_account_id is already a pool.
 * `client_secret` is the ONLY thing here that is ever sensitive: it is write-only (Fernet-encrypted
 * server-side) and NEVER echoed back by any endpoint, including this one's own success response.
 */
export interface PoolAddCredentialBody {
  account_name: string;
  account_number: string;
  zoom_account_id: string;
  client_id: string;
  client_secret: string;
}

/** The three expiry actions (pool.expiry_action enum). NULL on the wire means "not yet decided" —
 *  the job defaults an unset action to `license_only`, but the FE must show that as unset, not chosen. */
export type PoolExpiryAction = "grace_2stage" | "disassociate_now" | "license_only";

export const POOL_EXPIRY_ACTIONS = [
  "license_only",
  "grace_2stage",
  "disassociate_now",
] as const satisfies readonly PoolExpiryAction[];

/** GET /api/v1/pool/users?q=&account_id=&license=&limit= — one row per customer x current Zoom
 *  membership (LEFT JOINed; a customer with an open period but NO membership still appears —
 *  account_id/user_type/role_id all null). `account_id` (rev 6 R5/R6) is an EXACT filter, not ILIKE —
 *  it's how a Pool Accounts row click (`/pool/users?account=<account_id>`) narrows this screen to one
 *  account; combines with `q` rather than replacing it. `license` (rev 9, 21/7) is `"2"` (Licensed) or
 *  `"1"` (Basic) — omitted entirely means no license filter (All). `limit` caps at 5000 server-side
 *  (raised from 500 the same day) — the FE always requests the max so the table's own result count is
 *  the TRUE total, never a silently truncated page. */
export interface PoolUser {
  email: string;
  account_id: string | null;
  account_number: string | null;
  account_name: string | null;
  /** Zoom user type: 1=Basic, 2=Licensed. Null when there is no current membership. */
  user_type: number | null;
  user_status: string | null;
  /** '0'=owner '1'=admin '2'=member; null when there is no current membership. */
  role_id: string | null;
  last_seen_at: string | null;
  period_id: number | null;
  period_start: string | null;
  period_end: string | null;
  renew: boolean | null;
  expiry_action: PoolExpiryAction | null;
  grace_days: number | null;
  grace_started_on: string | null;
  /** 30/7 (pool_0009) — the PLANNED next round, set when `renew` is true and promoted to a real period
   *  by the 00:01 cron on `next_period_start`. Until then `period_start`/`period_end` above still
   *  describe the round the customer is IN: before this existed, saying Yes closed the current round on
   *  the spot and the row jumped a year ahead (คุณใหม่ 30/7 bug 2). Both null unless renew is true. */
  next_period_start: string | null;
  next_period_end: string | null;
  /** rev 15 — can the write engine act on this user RIGHT NOW? false = a master-account member
   *  with no fresh ZPCD-mirror row yet (created today; the mirror lands after the ~07:05 morning
   *  sync) — the exact state the tripwire skips. Surfaced so the operator learns BEFORE clicking
   *  force, not after. Standalone + membership-less rows always read true. */
  mirror_ready: boolean;
  /** rev 17b (F2) — is this customer's current account a STANDALONE pool account (credential-added,
   *  R4), as opposed to a ZPCD-mirrored master sub-account? Standalone write support is
   *  fail-closed off by default (`POOL_STANDALONE_WRITE_ENABLED`), so a force/unlink/bulk action on
   *  a `true` row is very likely to come back `skip_standalone_unsupported` — the UI warns BEFORE
   *  the click rather than letting the operator learn only from the outcome (same "announce, don't
   *  block" precedent as `mirror_ready` above). `false` for a master-account row or a period-only
   *  row with no current membership at all (nothing standalone to warn about). */
  standalone: boolean;
}

export interface PoolUsers {
  users: PoolUser[];
}

/** One row of `pool.period` (open or closed — history keeps closed rows). */
export interface PoolPeriod {
  id: number;
  email: string;
  sold_account_id: string | null;
  period_start: string;
  period_end: string;
  renew: boolean | null;
  expiry_action: PoolExpiryAction | null;
  grace_days: number | null;
  grace_started_on: string | null;
  /** 30/7 (pool_0009) — see PoolUser.next_period_start. On a CLOSED row these are history: the plan this
   *  round was renewed into (`close_reason: 'renewed'`). */
  next_period_start: string | null;
  next_period_end: string | null;
  closed_at: string | null;
  close_reason: string | null;
  set_by: string | null;
  updated_at: string;
}

/** One row of `pool.audit_log` — covers human actions, system/expiry-job writes, AND sweep observations
 *  (`observed.*`). `result`/`http_status`/`zoom_code`/`error`/`completed_at` are null until the Zoom call
 *  the row records has actually settled (the two-phase write the plan's guardrail #8 depends on). */
export interface PoolAuditEntry {
  id: number;
  ts: string;
  /** An email, "system", or "sweep". */
  actor: string;
  action: string;
  target_email: string | null;
  /** Set only on an `observed.renamed` row — the email this customer used to have. */
  target_email_old: string | null;
  target_account_id: string | null;
  /** Human account label resolved server-side from `target_account_id` (audit.py's own writers never
   *  know it — it's a JOIN at read time) — both null when there is no such account (e.g. the account
   *  has since been removed from the pool, or this row never had one). */
  target_account_number?: string | null;
  target_account_name?: string | null;
  period_id: number | null;
  /** Real inserts (audit.py::insert_audit callers) always log an ARRAY of `{field, old, new}` — see
   *  poolAuditFormat.ts's formatAuditDetails() for the primary rendering. The bare-object/other-JSON
   *  branches there are a defensive fallback, not something any current write site produces. */
  details: Record<string, unknown> | unknown[] | null;
  /** step 7 sets this from the current membership row; step 10 backfills a live value from Zoom's own
   *  response body. Null for a row that was never a Zoom-write (e.g. operator.grant). */
  zoom_user_id?: string | null;
  /** 'cron' | 'force' — which path wrote this row. Null for a plain human action (nothing "triggered" it
   *  — the click itself is the trigger). */
  trigger?: string | null;
  /** Written BEFORE the Zoom call fires (write-ahead intent, guardrail #8) — null for a row that was
   *  never a two-phase write. */
  intent_at?: string | null;
  result: string | null;
  http_status: number | null;
  zoom_code: number | null;
  error: string | null;
  completed_at: string | null;
  /** Request metadata for a human-triggered row; null/absent for actor='system'/'sweep' (audit_log's own
   *  `ip TEXT, user_agent TEXT, request_id TEXT` comment: "NULL เมื่อ actor='system'/'sweep'"). */
  ip?: string | null;
  request_id?: string | null;
}

/** GET /api/v1/pool/customers/{email}/history */
export interface PoolCustomerHistory {
  email: string;
  periods: PoolPeriod[];
  audit: PoolAuditEntry[];
}

/** POST /api/v1/pool/periods — 201; 409 = an open period already exists for this email.
 *  sold_account_number is what an operator actually KNOWS (the backend resolves it to the internal
 *  account_id; 422 when it matches no pool). sold_account_id stays for programmatic callers. */
export interface PoolCreatePeriodBody {
  email: string;
  period_start: string;
  period_end: string;
  sold_account_id?: string;
  sold_account_number?: string;
  /**
   * r10 22/7 (principal: "อย่างอื่นก็ควรทำพร้อมกำหนด period เลย") — the ONE-SHOT create form sends the renew
   * decision atomically with period creation instead of requiring a reopen. SAME validation as
   * POST /renew-decision (PoolRenewDecisionBody): `expiry_action` is required when `renew` is `false`,
   * and `grace_days` (positive) is additionally required when that action is `grace_2stage`. Omitted/
   * `null` = Undecided, identical in meaning to a period that has never had a renew decision set.
   */
  renew?: boolean | null;
  expiry_action?: PoolExpiryAction;
  grace_days?: number;
  /**
   * 30/7 (คุณใหม่ bug 1) — REQUIRED when `renew` is true, and REFUSED (422) otherwise: the next round's
   * end date. Its start is never sent — the server derives `period_end + 1` (periods.py). Before this,
   * a renew=true create 422'd with "ใช้ปุ่มต่ออายุ (Renew)", a button the create form does not have.
   */
  next_period_end?: string;
}

export interface PoolCreatePeriodResult {
  period_id: number;
  email: string;
  /** Present only when the body carried a renew decision (see next_period_end above). */
  next_period_start?: string | null;
  next_period_end?: string | null;
}

/** PATCH /api/v1/pool/periods/{id} */
export interface PoolUpdatePeriodBody {
  period_start?: string;
  period_end?: string;
}

/**
 * POST /api/v1/pool/periods/{id}/renew-decision — sets what happens AT expiry (NOT an immediate action).
 * 422 on a bad combination (e.g. grace_2stage with no grace_days). Any other 4xx the server returns is
 * still surfaced verbatim by the caller, not swallowed into a generic failure.
 */
export interface PoolRenewDecisionBody {
  renew: boolean | null;
  expiry_action?: PoolExpiryAction;
  grace_days?: number;
  /** 30/7 — required when `renew` is true (see PoolCreatePeriodBody.next_period_end). The response
   *  carries `rolled_forward: true` in the one case the server acts immediately instead of parking the
   *  plan: the current round had ALREADY expired, so the planned one starts today or earlier. */
  next_period_end?: string;
}

export interface PoolRenewDecisionResult {
  period_id: number;
  renew: boolean | null;
  expiry_action: PoolExpiryAction | null;
  next_period_start: string | null;
  next_period_end: string | null;
  /** true ONLY when the current round had already expired, so the planned one started today or earlier
   *  and the server promoted it in the same request instead of leaving it for the cron. */
  rolled_forward: boolean;
  new_period_id?: number;
}

/** POST /api/v1/pool/periods/{id}/renew — closes the CURRENT period and opens a new one immediately.
 *  30/7: this is now the EARLY-renewal door only (start the next round before the current one runs
 *  out). The ordinary "Yes, they're renewing" is renew-decision + next_period_end, which waits for the
 *  day — making this endpoint the default is what jumped the screen a year ahead (bug 2). */
export interface PoolRenewBody {
  new_start: string;
  new_end: string;
}

export interface PoolRenewResult {
  closed_period_id: number;
  new_period_id: number;
}

/** POST /api/v1/pool/periods/{id}/close — a reason is mandatory (this closes the business record). */
export interface PoolCloseBody {
  reason: string;
}

/** The two grantable pool roles (`pool.operator.role`) — Owner is never one of these; it is implicit
 *  and carries no row (see PoolAccess). */
export type PoolOperatorRole = "admin" | "viewer";

/** GET /api/v1/pool/operators — one row per email that has REAL owner rights (a portal Role.OWNER),
 *  pinned at the top of the Settings list, badge "Owner", not editable or removable: there is no
 *  underlying `pool.operator` row to revoke. */
export interface PoolOwnerRow {
  email: string;
}

/** pool.operator row — the allow-list gating every /pool/* route below GET /access. Grant/revoke is
 *  `_manage` (Owner or pool-Admin), never Owner-only (rev 6 R2) — an Owner is always implicit anyway, so
 *  there is no bootstrap deadlock to worry about. */
export interface PoolOperatorRow {
  email: string;
  role: PoolOperatorRole;
  granted_by: string;
  granted_at: string;
}

/** GET /api/v1/pool/operators — `owners` (from `public.app_user` role=owner) ALWAYS pinned above
 *  `operators` in the Settings UI; only `operators` rows can be revoked. */
export interface PoolOperators {
  owners: PoolOwnerRow[];
  operators: PoolOperatorRow[];
}

/** POST /api/v1/pool/operators — 422 when `email` already belongs to a portal Owner (rev 6 R1: an
 *  Owner already has full access implicitly and is never listed — granting them explicitly would only
 *  confuse the list). */
export interface PoolAddOperatorBody {
  email: string;
  role: PoolOperatorRole;
}

/**
 * GET /api/v1/pool/user-candidates?q= (rev 6 R3, gate `_manage`) — search `public.app_user` by email
 * (ILIKE, is_active only) to back the Settings "grant access" picker, replacing the old free-typed
 * email input. `portal_role` renders with the same `role.*` copy as everywhere else in the portal;
 * `is_pool_operator` (already has SOME pool row, or is an Owner) disables granting them again.
 */
export interface PoolUserCandidate {
  email: string;
  portal_role: Role | null;
  is_pool_operator: boolean;
}

export interface PoolUserCandidates {
  candidates: PoolUserCandidate[];
}

/** A row in either dashboard mismatch card. Money-free; the shared fields are enough to link back into
 *  Pool Users filtered by email. `[key: string]: unknown` tolerates each card's extra columns (the two
 *  cards are different SQL queries with different incidental fields). */
export interface PoolDashboardRow {
  email: string;
  account_id: string | null;
  account_number: string | null;
  account_name: string | null;
  [key: string]: unknown;
}

export interface PoolDashboardCard {
  count: number;
  rows: PoolDashboardRow[];
}

/**
 * GET /api/v1/pool/dashboard — the two mismatch cards (design §10), the module's whole reason for
 * existing: card 1 is "Zoom has it, we don't" (a licensed non-owner/admin Zoom seat with no open period
 * — pure revenue leak); card 2 is "we have it, Zoom doesn't" (an open period whose customer has no
 * Licensed membership — a paying customer who can't use what they bought). Card 2 only ever fills
 * because the sweep NEVER auto-closes a period on its own observation (design §7's "never overwrite"
 * rule) — an empty card 2 is the rule working, not a sign nothing needs it.
 */
export interface PoolDashboard {
  card1_license_without_period: PoolDashboardCard;
  card2_period_without_license: PoolDashboardCard;
  card3_two_clocks_conflict: { count: number; rows: PoolTwoClocksConflictRow[] };
  card4_renew_yes_stalled: { count: number; rows: PoolRenewStalledRow[] };
  card5_observed_7d: { rows: PoolObservedRow[] };
  tripwire_queue: { count: number; rows: PoolTripwireRow[] };
}

export interface PoolSweepRun {
  id: number;
  status: string;
  started_at: string;
  finished_at: string | null;
  accounts_ok: number;
  accounts_target: number;
}

/** GET /api/v1/pool/status — feeds FreshnessBadge (kind="mirror") from `last_complete.finished_at`,
 *  the last sweep that actually finished 'complete' (as opposed to `last_run`, which may be running or
 *  partial_aborted). */
export interface PoolStatus {
  last_run: PoolSweepRun | null;
  last_complete: { id: number; finished_at: string } | null;
}

/**
 * POST /api/v1/pool/sweep/run (rev 8, "Sync now" — Pool Accounts header, `_manage`-gated) — enqueues an
 * out-of-cadence sweep run instead of waiting for the regular schedule. `enqueued: true` is the ONLY
 * success shape (never the run's own final state — the caller polls GET /pool/status afterwards to see it
 * land, see useRunPoolSweep in features/pool/hooks.ts). 409 = a sweep is already running — an expected,
 * non-broken outcome, rendered as an informational toast, never an error state.
 *
 * rev 17c (23/7) — `in_pause_window` (optional: older server builds may not send it at all, which the FE
 * treats identically to `false`) is true when the enqueue landed inside the daily 02:00–08:00 pause
 * window; the server accepts the call but the run itself is skipped, so there is nothing to poll for —
 * useRunPoolSweep short-circuits straight to an informational toast instead of entering its running/poll
 * state.
 */
export interface PoolSweepEnqueueResult {
  enqueued: true;
  in_pause_window?: boolean;
}

/**
 * GET /api/v1/pool/audit?email=&actor=&action=&date_from=&date_to=&before_id=&limit= — the Pool
 * module's own Audit log tab (SaaS-standard: filters + keyset "Load more", distinct from the portal-wide
 * /activity feed). `email` matches target_email OR target_email_old (same as the customer-history
 * lookup); `actor` is an EXACT match (mirrors the portal-wide /activity actor filter, not ILIKE);
 * `before_id` is the keyset cursor — the last row's `id` from the previous page; the feed is
 * commit-ordered (BE sorts by id DESC alone, keeping the cursor exact). No `next_cursor`/`has_more` flag on
 * this envelope — a page shorter than `limit` IS the end-of-results signal (see usePoolAuditFeed).
 */
export interface PoolAuditParams {
  email?: string;
  /** Exact match, lower-cased server-side — not a substring search. */
  actor?: string;
  action?: string;
  date_from?: string;
  date_to?: string;
  before_id?: number;
  limit?: number;
}

export interface PoolAudit {
  audit: PoolAuditEntry[];
}

// ---------------------------------------------------------------------------------------------------
// Pool module — Reports tab ("Online account history", 30/7) — a daily snapshot of the whole pool
// estate (taken 23:00, see the snapshot.daily audit action) so an operator can see the estate's
// license/seat picture on any past day, not just today's live GET /pool/accounts. `_manage`-gated,
// same as every other write-adjacent Pool screen (this is read-only, but the underlying snapshot data
// is operationally sensitive the same way the Dashboard's own numbers are).
//
// GET /pool/reports/daily?start=&end= — the LIST view. Aggregation MUST mirror
// features/pool/poolAccountStats.ts's aggregatePoolStats() exactly (seats/assigned/unassigned/
// utilization_pct — see that file's own banner for the null-handling + non-clamping rules); a day's
// row here is that formula applied to whatever accounts existed in the pool on THAT day, not today's.
// GET /pool/reports/daily/{date} — the DAY-DRILL-DOWN view — 404 when no snapshot exists for that date
// (the sweep runs nightly; a date before the module existed, or a day the sweep skipped, has none).
// ---------------------------------------------------------------------------------------------------

/** One row of GET /pool/reports/daily's `days` array — a single day's aggregate, DESC by
 *  snapshot_date. Same null/negative semantics as `PoolStatAggregate` (poolAccountStats.ts): `seats`
 *  null when no account that day had a recorded seat count, `unassigned`/`utilization_pct` null only
 *  when `seats` is, `unassigned` never clamped at 0. */
export interface PoolReportDay {
  snapshot_date: string;
  accounts: number;
  seats: number | null;
  assigned: number;
  unassigned: number | null;
  utilization_pct: number | null;
}

export interface PoolReportsDaily {
  days: PoolReportDay[];
  start: string;
  end: string;
}

/** Query params for GET /pool/reports/daily — both optional; the server defaults `end` to today
 *  (Asia/Bangkok) and `start` to `end - 29 days` when omitted. The Reports list screen always sends
 *  both explicitly (its own DateRangePicker default), so the server default is a fallback for a bare
 *  API call, not something the FE relies on. */
export interface PoolReportsDailyParams {
  start?: string;
  end?: string;
}

/** One account row inside GET /pool/reports/daily/{date} — the day-snapshot's own copy of an
 *  account's Seats/Assigned figures, NOT the live `PoolAccount` (no `added_by`/`manual_*`/subscription
 *  fields — a historical snapshot doesn't carry today's admin metadata). Narrow enough to satisfy
 *  poolAccountStats.ts's `aggregatePoolStats`/`bucketByTier` (both widened, 30/7, to accept exactly
 *  this shape) so the day-view's "By account type" breakdown shares the Dashboard's own formula. */
export interface PoolReportAccountRow {
  account_id: string;
  account_number: string | null;
  account_name: string | null;
  account_type: string | null;
  hosts_total: number | null;
  members: number;
  licensed_members: number;
  under_sub_account: boolean;
  enabled: boolean;
}

/** GET /pool/reports/daily/{date} — 404 when no snapshot exists for that date (see this section's own
 *  banner). `accounts` ordered account_name NULLS LAST, then account_id (server-side; the FE renders
 *  the array as-received). */
export interface PoolReportDayDetail {
  snapshot_date: string;
  captured_at: string;
  accounts: PoolReportAccountRow[];
}

// ---------------------------------------------------------------------------------------------------
// Pool module Phase 2 — the intents queue + force actions + dashboard cards 3-5 + the tripwire queue.
//
// The sweep records a two-phase "intent" audit row (see PoolAuditEntry's completed_at banner) BEFORE it
// calls Zoom, then completes it once the call settles. If the worker crashes between those two writes,
// the intent is left OPEN — GET /intents lists every such row so a human can check Zoom directly and say
// what actually happened (POST .../resolve). `stale` flips true after ~10 minutes with no resolution —
// a strong crash signal, not a certainty, so the UI highlights it rather than auto-resolving anything.
// ---------------------------------------------------------------------------------------------------

/** The two actions an intent can be "in flight" on. */
export type PoolIntentAction = "license.remove" | "user.unlink";

/** GET /api/v1/pool/intents — one row per OPEN (unresolved) intent. */
export interface PoolIntent {
  id: number;
  ts: string;
  action: PoolIntentAction;
  target_email: string | null;
  target_account_id: string | null;
  period_id: number | null;
  details: Record<string, unknown> | null;
  zoom_user_id: string | null;
  trigger: string;
  intent_at: string;
  /** True once the intent has been open ~10 minutes with no resolution — usually a crashed worker. */
  stale: boolean;
}

export interface PoolIntents {
  intents: PoolIntent[];
}

export type PoolIntentResolution = "resolved_success" | "resolved_failed";

/**
 * POST /api/v1/pool/intents/{audit_id}/resolve — a human declares what Zoom ACTUALLY did, since the
 * sweep itself could not tell (the crash happened between the intent write and the completion write).
 * 404 = this intent was already resolved (or never existed); 409 = its period_id is missing, so the
 * consequence cannot be applied; 422 = a blank note (1-500 chars required — this is the business record
 * of why a human overrode the sweep's own account of events).
 */
export interface PoolResolveIntentBody {
  success: boolean;
  note: string;
}

export interface PoolResolveIntentResult {
  intent_id: number;
  result: PoolIntentResolution;
  /** Human-readable description of what happens next (e.g. "won't retry" / "retries next sweep") —
   *  shown verbatim in the success toast, never paraphrased. */
  consequence: string;
}

/**
 * POST /api/v1/pool/periods/{id}/force-license-remove and .../force-unlink — an operator-forced action
 * that skips the sweep's own cadence. 404 = no open period for this id; 409 = an open intent already
 * exists for this period (resolve it first, so two writers can't race the same Zoom call) —
 * force-unlink ALSO 409s when the pool has (or the managed-domain check is unknown/stale —
 * fail-closed, same rule as the renew-decision unlink gate) managed domains; 503 = the Zoom app has no
 * S2S credentials configured.
 */
export interface PoolForceActionResult {
  period_id: number;
  dry_run: boolean;
  /** Free-form counters, e.g. `{ license_removed: 1 }` — the key vocabulary is server-defined and not
   *  enumerable client-side, so this renders as a generic compact list rather than named columns. */
  actions: Record<string, number>;
}

/**
 * POST /api/v1/pool/users/bulk-force (rev 17b — PINNED CONTRACT, rev17b-addendum.md) — the
 * membership-keyed (period-less) sibling of force-license-remove/force-unlink above. ONE endpoint for
 * both the bulk actions AND the single-row UnlinkOnlyDialog (N=1 of the same call — there is no
 * separate singular endpoint). `_manage` only (owner ∪ pool-admin).
 *
 * Note the shape is DELIBERATELY different from PoolForceActionResult above: `dry_run` is top-level
 * (the resolved ExpiryState for the whole request, not per-email), and `results` is a FLAT
 * `{email: code}` map — ONE outcome-code string per email, not a counter map. `summary` is the same
 * codes tallied by count, for the headline "N succeeded · N skipped · N failed" line.
 */
export type PoolBulkForceAction = "unlink" | "license_remove";

/** `emails`: the caller sends whatever it collected client-side; the server lowercases + dedups and
 *  enforces `1 ≤ len ≤ pool_expiry_blast_cap` (422 outside that range) — the FE never sends more than
 *  `POOL_BULK_CHUNK_SIZE` (hooks.ts) at once regardless, chunking a larger selection sequentially. */
export interface PoolBulkForceBody {
  action: PoolBulkForceAction;
  emails: string[];
}

/**
 * Always HTTP 200 (even with every email skipped/failed — partial outcomes are the norm, exactly like
 * PoolForceActionResult). Request-level refusals (503 kill-switch off, 409 stale sweep, 422 size/empty/
 * unknown action) throw before any email is touched and carry NO `results` — see
 * features/pool/bulkForceAbort.ts for the one shared renderer both dialogs use for those.
 */
export interface PoolBulkForceResult {
  dry_run: boolean;
  /** email(lowercased) -> ONE outcome code — reuses the exact ForceActionDialog OUTCOME_KEY vocabulary
   *  (features/pool/forceOutcome.ts) plus `skip_has_open_period` (a period-holder must go through
   *  PeriodModal's own force buttons instead — see the Manage-every-row matrix). */
  results: Record<string, string>;
  /** The same codes as `results`, tallied by count — the summary line's source. */
  summary: Record<string, number>;
}

/** Dashboard card 3: an open period whose OWN end date and Zoom's own subscription-end date disagree —
 *  two clocks that should track together but silently drifted apart. */
export interface PoolTwoClocksConflictRow {
  email: string;
  period_id: number;
  period_end: string;
  account_id: string | null;
  account_number: string | null;
  account_name: string | null;
  zoom_subscription_end: string | null;
}

/** Dashboard card 4: renew=true (the customer IS renewing) but nothing has moved the period forward yet —
 *  a stalled renewal that expires anyway if nobody acts on it.
 *
 *  30/7 — two different stories share this card, told apart by next_period_start:
 *    set  → the cron should have promoted this round the next day; still here after 2 days = the
 *           ROLL-OVER is stuck (a system defect).
 *    null → a legacy renew=true row from before pool_0009 (new ones are impossible — CHECK). That one
 *           IS "someone forgot", and keeps the original 30-day window. */
export interface PoolRenewStalledRow {
  email: string;
  period_id: number;
  period_end: string;
  next_period_start: string | null;
  next_period_end: string | null;
}

/** Dashboard card 5: a 7-day tally of sweep-observed actions (`observed.*` audit rows), by action name. */
export interface PoolObservedRow {
  action: string;
  n: number;
}

/** The tripwire queue: conditions the watchdog has flagged as seen (and possibly already alerted on).
 *  `condition_key` is a composite server key; the FE only needs the email segment of it. */
export interface PoolTripwireRow {
  condition_key: string;
  first_seen_at: string;
  last_alerted_at: string | null;
}

// ---------------------------------------------------------------------------------------------------
// Pool module — self-service access check + system control (rev 6: gated on the POOL role, not on
// Role.OWNER identity — see the R2 banner above and POOL-UI-REV6-SPEC.md).
//
// GET /pool/access is the ONE /pool/* route every signed-in staffer may call regardless of pool role:
// it NEVER 403s. It exists so the FE can decide nav VISIBILITY and every tab/section gate (see
// PoolLayout, AppShell) without itself becoming another 403 surface. `role` is this caller's OWN pool
// role — `"owner"` (implicit, always, for a portal Role.OWNER), `"admin"`/`"viewer"` (from their
// `pool.operator` row), or `null` (not an operator at all — every /pool/* route below 403s them).
// `open_intents` is the open-intents count (0 for `"viewer"`/`null` — they can never see that queue).
//
// GET/PUT /pool/system-state arm/disarm the license-removal job itself — genuinely dangerous (LIVE
// removes real customer licenses) — gated `_manage` (pool role owner OR admin; rev 5 was Owner-only,
// rev 6 R2 extended it to pool-Admin on the principal's explicit instruction after a risk flag).
// ---------------------------------------------------------------------------------------------------

/** GET /api/v1/pool/access — never 403s. `role: null` reads exactly like a non-operator always has. */
export interface PoolAccess {
  role: PoolRole;
  open_intents: number;
}

/** The three states the license-removal job can be armed to. */
export type PoolSystemStateName = "off" | "dry_run" | "live";

/** Where the current value came from: a hard .env break-glass override, a real `db` row someone has
 *  actually set, or `settings` (no row yet — the server's static config default). */
export type PoolSystemStateSource = "hard_disable" | "db" | "settings";

/** GET /api/v1/pool/system-state — `_manage` only (403 for a pool-Viewer or a non-operator). */
export interface PoolSystemState {
  enabled: boolean;
  dry_run: boolean;
  state: PoolSystemStateName;
  source: PoolSystemStateSource;
  updated_by: string | null;
  updated_at: string | null;
  /** True when the .env break-glass is forcing the system off — every write 409s while this is true. */
  hard_disabled: boolean;
}

/**
 * PUT /api/v1/pool/system-state — `_manage` only. Arming LIVE (`enabled:true, dry_run:false`) REQUIRES
 * `confirm:"LIVE"` (exact, case-sensitive) or the server 422s with a Thai message the caller renders
 * VERBATIM; any write while `hard_disabled` 409s, also rendered verbatim.
 */
export interface PoolSetSystemStateBody {
  enabled: boolean;
  dry_run: boolean;
  confirm?: string;
}

export interface PoolSetSystemStateResult {
  state: PoolSystemStateName;
  enabled: boolean;
  dry_run: boolean;
}

// ---------------------------------------------------------------------------------------------------
// Activity feed (GET /api/v1/activity) — Owner/Admin (audit:read). auth_log + audit_log, keyset-paged.
// ---------------------------------------------------------------------------------------------------

export type ActivitySource = "auth" | "audit";

export type ActivityKind =
  | "login_success"
  | "login_failed"
  | "logout"
  | "data_access"
  | "money_access"
  | "admin_action"
  | "sync_now";

/** Server-decoded category (a superset of ActivityKind + "other" for unknown Auth0 codes). */
export type ActivityCategory = ActivityKind | "other";

export interface ActivityItem {
  source: ActivitySource;
  id: string;
  /** tz-aware ISO; null only for an Auth0 row with no timestamp (sorted last). */
  at: string | null;
  actor_email: string | null;
  /** Raw Auth0 type code or audit action string. */
  event_code: string | null;
  /** Human label decoded by the server. */
  label: string;
  category: ActivityCategory;
  target_account_id: string | null;
  /** Human account name/number resolved server-side from sub_accounts (null for a churned account). */
  target_account_name: string | null;
  target_account_number: string | null;
  client_ip: string | null;
  user_agent: string | null;
  money: boolean | null;
  description: string | null;
  /** Structured admin-action metadata (null for plain reads / auth rows). */
  detail: Record<string, unknown> | null;
}

export interface ActivityFeed {
  items: ActivityItem[];
  /** Opaque keyset cursor for the next (older) page; null when no more. */
  next_cursor: string | null;
  has_more: boolean;
  limit: number;
}

export interface ActivityParams {
  limit?: number;
  cursor?: string | null;
  kind?: ActivityKind | null;
  actor?: string | null;
  date_from?: string | null;
  date_to?: string | null;
}

// ---------------------------------------------------------------------------------------------------
// Sync-now (POST /api/v1/sync-now/...) — the SAME envelope for every outcome
// ---------------------------------------------------------------------------------------------------

export type SyncNowStatus =
  | "ok"
  | "deduped"
  | "blocked_quota"
  | "blocked_throttle"
  | "blocked_daily_sync"
  | "error";

export type SyncNowSurface = "plans_usage" | "users" | "accounts_list";

/** The Heavy+RI quota snapshot returned on every sync-now outcome (render LOUD on a block). */
export interface QuotaSnapshot {
  nightly_used: number;
  syncnow_used: number;
  total_used: number;
  cap: number;
  reserve: number;
  on_demand_budget: number;
  on_demand_remaining: number;
  cap_remaining: number;
  nightly_source_available: boolean;
}

export interface SyncNowEnvelope {
  status: SyncNowStatus;
  blocked: boolean;
  surface: SyncNowSurface;
  account_id: string | null;
  /** Structured machine reason on a block (render it loudly, mapped to a Thai message). */
  reason: string | null;
  api_calls_spent: number;
  /**
   * The SEPARATE "live just now" freshness instant (tz-aware ISO-8601). Set ONLY when status==='ok'.
   * NEVER reuse it as the mirror or mirror-health timestamp.
   */
  live_at: string | null;
  quota: QuotaSnapshot | null;
  data: Record<string, unknown> | null;
}

// ---------------------------------------------------------------------------------------------------
// Mirror health — a SEPARATE freshness signal (poller-current vs ZPCD), modelled from
// mirror/health.py MirrorHealth. (No HTTP route in M1-M3 yet; mocked for the FreshnessBadge.)
// ---------------------------------------------------------------------------------------------------

export interface MirrorHealth {
  healthy: boolean;
  stale: string[];
  errored: string[];
  missing: string[];
  /** When the poller-health was last evaluated (tz-aware ISO). */
  checked_at: string;
}

// ---------------------------------------------------------------------------------------------------
// Health (GET /health, /health/ready) — unauthenticated
// ---------------------------------------------------------------------------------------------------

export interface HealthReady {
  status: "ready" | "degraded";
  checks: Record<string, string>;
}

// ---------------------------------------------------------------------------------------------------
// Error envelope (normalized)
// ---------------------------------------------------------------------------------------------------

/**
 * The backend uses two error shapes: the custom handler `{error, message, detail?}` (422/500) and the
 * FastAPI HTTPException default `{detail}` (401/403/404/429/502). We normalize both into this.
 */
export interface ApiError {
  /** HTTP status, or 0 for a network/transport failure. */
  status: number;
  /** Machine code: backend `error`, the syncnow `reason`, or a synthetic "http_<status>". */
  code: string;
  /** Human message (backend `message`/`detail`, or a fallback). */
  message: string;
  /** Request id from the X-Request-ID response header, for support correlation. */
  requestId?: string;
  /**
   * True when the failed request DID carry a Bearer token (a token was attached and the backend still
   * rejected it), false when no Authorization header was sent (the token getter returned null — the
   * silent renewal failed; `renewalFailure` says why). Lets a 401 handler tell a token-present rejection
   * (possibly transient — Auth0 key rotation / clock skew) from a request that never had a token at all.
   * Undefined for status 0 (never reached the backend) and for anonymous calls.
   */
  authAttempted?: boolean;
  /**
   * Why the silent token renewal failed for THIS request, captured the instant the token getter returned
   * null — not looked up later. The record it comes from is a single global slot, and auth0-spa-js
   * dedupes only strictly-concurrent renewals, so a second renewal starting milliseconds later is a
   * separate call that can succeed and wipe the slot before this request's 401 even arrives. Reading it
   * at 401 time therefore loses the verdict in exactly the concurrent case it was written for; riding on
   * the error, it cannot be overwritten by anyone else's renewal. Null when a token WAS sent (the
   * renewal plainly worked) or when there was no fresh record to attach; undefined for status 0 and for
   * anonymous calls.
   */
  renewalFailure?: RenewalFailure | null;
  /** Raw validation detail when present (422). */
  detail?: unknown;
}
